import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { Subscription, timer } from 'rxjs';

import { ApAnalyticsService, ClickAnalyticsData } from '@ap/analytics';
import { ApContentService } from '@ap/content';
import { ModalContent } from '@ap/modal';

import {
    MijnAnalyseFormFields,
    MijnAnalyseNotification,
    MijnAnalyseNotificationState,
    MijnAnalyseNotificationTypes,
    MijnAnalyseOptions
} from './mijn-analyse.modal.model';

@Component({
    selector: 'mijn-analyse-modal',
    templateUrl: './mijn-analyse.modal.component.html',
    standalone: false
})
export class MijnAnalyseModalComponent extends ModalContent<MijnAnalyseOptions> implements OnInit {
    public analyseForm: UntypedFormGroup;

    public emailstring = '';
    public keyNotificationMap: Map<string, MijnAnalyseNotification>;
    public shownNotificationKey = MijnAnalyseNotificationTypes.arrival;

    public loading: boolean;
    private readonly newline = '%0D%0A';
    private subscriptions = new Subscription();

    public constructor(
        private readonly analyticsService: ApAnalyticsService,
        private readonly clipboard: Clipboard,
        private readonly contentService: ApContentService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.loading = true;
        this.subscriptions.add(timer(500).subscribe(() => (this.loading = false)));
        this.initFormGroup();
        this.initNotifications();
    }

    public getEmailForBrand(): { email: string } {
        return { email: this.contentService.get('mijn.analyse.klantenservice.email') };
    }

    public copyToClipboard(): void {
        const clipboardContent = this.getInformationFromForm('copy');
        this.clipboard.copy(`Onderwerp: ${clipboardContent.subject}\nWat gaat er fout:\n${clipboardContent.body}\n`);
        this.shownNotificationKey = MijnAnalyseNotificationTypes.copy;
        this.analyticsService.push(new ClickAnalyticsData('mijn-analyse', 'copy'));
    }

    public getFooterHelpText(): string {
        return this.contentService.get('mijn.analyse.modal.button.explanation', this.getEmailForBrand());
    }

    public openEmail(): void {
        if (this.analyseForm.valid) {
            window.open(this.emailstring);
            this.analyticsService.push(new ClickAnalyticsData('mijn-analyse', 'email'));
        } else {
            Object.keys(this.analyseForm.controls).forEach((field) => {
                const control = this.analyseForm.get(field);
                control.markAsTouched();
                control.markAsDirty();
            });
        }
    }

    private initFormGroup(): void {
        this.analyseForm = new FormGroup<any>({ body: new FormControl(), subject: new FormControl() });

        this.updateEmailString();
        this.analyseForm.valueChanges.subscribe(() => {
            this.updateEmailString();
        });
    }

    private updateEmailString(): void {
        const info = this.getInformationFromForm('email');
        this.emailstring = `mailto:${this.contentService.get('mijn.analyse.klantenservice.email')}?subject=${info.subject}&body=${
            info.body
        }`;
    }

    private getInformationFromForm(type: 'email' | 'copy'): MijnAnalyseFormFields {
        const enter = type === 'email' ? this.newline : '\n';

        let msg = this.analyseForm.get('body').value;
        if (msg !== null) {
            msg = msg.replace('\n', enter);
        }

        let body = `${msg}${enter}${enter}Technische informatie:${enter}`;

        Object.keys(this.modalOptions.options).forEach((key) => {
            const child = this.modalOptions.options[key];
            body += `${child.label}: ${child.value}${enter}`;
        });

        const subject = `${this.contentService.get('mijn.analyse.klantenservice.email.tag')} ${
            this.modalOptions.options.customerID.value
        } - ${this.analyseForm.get('subject').value}`;

        return { subject, body };
    }

    private initNotifications(): void {
        this.keyNotificationMap = new Map<string, MijnAnalyseNotification>([
            [
                MijnAnalyseNotificationTypes.copy,
                {
                    closable: false,
                    state: MijnAnalyseNotificationState.SUCCESS,
                    text: this.contentService.get('mijn.analyse.modal.button.copy.notificationtext', this.getEmailForBrand())
                }
            ],
            [
                MijnAnalyseNotificationTypes.arrival,
                {
                    closable: false,
                    state: MijnAnalyseNotificationState.INFO,
                    text: this.contentService.get('mijn.analyse.modal.notification.info')
                }
            ]
        ]);
    }
}
